import { grey } from "@mui/material/colors";
import { ClientReport } from "api/models/Clients";
import { formatNumberWithDecimals } from "helpers/numbers";

export interface ClientReportRow {
  item: string;
  value?: string;
  formula?: string;
  color?: string;
  bold?: boolean;
}

const sectionRow = (item: string): ClientReportRow => ({
  item,
  color: grey[100],
  bold: true,
});

const valueRow = (
  item: string,
  value: number,
  formula: string,
): ClientReportRow => ({
  item,
  value: formatNumberWithDecimals(value, item.endsWith("(MWh)") ? 3 : 2),
  formula,
});

const balanceEnergyRows = (report: ClientReport): ClientReportRow[] => [
  sectionRow("Balance energy"),
  valueRow(
    "Balance energy purchased quantity (MWh)",
    report.balanceEnergy.purchasedQuantityMWh,
    "Σ (contracted − metered supply) over the MTUs where metered < contracted; contracted = Production forecast − Consumption forecast",
  ),
  valueRow(
    "Balance energy total purchase cost (EUR)",
    report.balanceEnergy.totalPurchaseCostEur,
    "Σ (purchased balance energy (MWh) × balancing price of the MTU)",
  ),
  valueRow(
    "Balance energy purchase cost (EUR/MWh)",
    report.balanceEnergy.purchasePriceEurPerMWh,
    "Balance energy total purchase cost (EUR) ÷ Balance energy purchased quantity (MWh)",
  ),
  valueRow(
    "Balance energy sold quantity (MWh)",
    report.balanceEnergy.soldQuantityMWh,
    "Σ (metered supply − contracted) over the MTUs where metered > contracted; contracted = Production forecast − Consumption forecast",
  ),
  valueRow(
    "Balance energy total sales revenue (EUR)",
    report.balanceEnergy.totalSalesRevenueEur,
    "Σ (sold balance energy (MWh) × balancing price of the MTU)",
  ),
  valueRow(
    "Balance energy sales price (EUR/MWh)",
    report.balanceEnergy.salesPriceEurPerMWh,
    "Balance energy total sales revenue (EUR) ÷ Balance energy sold quantity (MWh)",
  ),
  valueRow(
    "Average price (inc. balance energy) (EUR/MWh)",
    report.prices.averagePriceEurPerMWh,
    "(Forecasted production sales revenue − Forecasted consumption purchase cost − Balance energy total purchase cost + Balance energy total sales revenue) ÷ Metered supply (MWh)",
  ),
  valueRow(
    "Weighed average price (EUR/MWh)",
    report.prices.weightedAveragePriceEurPerMWh,
    "Metered supply (EUR) ÷ Metered supply (MWh)",
  ),
  valueRow(
    "Balance energy cost (EUR/MWh)",
    report.prices.balanceEnergyCostEurPerMWh,
    "Average price (inc. balance energy) − Weighed average price",
  ),
];

export const buildClientReportRows = (
  report: ClientReport,
): ClientReportRow[] => [
  sectionRow("Production"),
  valueRow(
    "Production quantity (MWh)",
    report.production.quantityMWh,
    "Σ metered net supply (produced − consumed) over the MTUs where it is positive",
  ),
  valueRow(
    "Production sales revenue (EUR)",
    report.production.salesRevenueEur,
    "Σ (metered net supply (MWh) × day-ahead price) over the producing MTUs",
  ),
  valueRow(
    "Production weighed average price (EUR/MWh)",
    report.production.weightedAveragePriceEurPerMWh,
    "Production sales revenue (EUR) ÷ Production quantity (MWh)",
  ),
  sectionRow("Consumption"),
  valueRow(
    "Consumption quantity (MWh)",
    report.consumption.quantityMWh,
    "Σ |metered net supply (produced − consumed)| over the MTUs where it is negative",
  ),
  valueRow(
    "Consumption purchase cost (EUR)",
    report.consumption.purchaseCostEur,
    "Σ (|metered net supply| (MWh) × day-ahead price) over the consuming MTUs",
  ),
  valueRow(
    "Consumption weighed average price (EUR/MWh)",
    report.consumption.weightedAveragePriceEurPerMWh,
    "Consumption purchase cost (EUR) ÷ Consumption quantity (MWh)",
  ),
  valueRow(
    "Consumption forecast (MWh)",
    report.consumptionForecast.forecastMWh,
    "Σ energy bought on Nord Pool (day-ahead + IDA-1…3) by the forecast sources whose net position in the MTU is a purchase",
  ),
  valueRow(
    "Forecasted consumption purchase cost (EUR)",
    report.consumptionForecast.forecastPurchaseCostEur,
    "Σ (bought energy (MWh) × trade price) over the consumption forecast trades",
  ),
  valueRow(
    "Forecasted consumption average purchase cost (EUR/MWh)",
    report.consumptionForecast.averagePurchasePriceEurPerMWh,
    "Forecasted consumption purchase cost (EUR) ÷ Consumption forecast (MWh)",
  ),
  sectionRow("Production"),
  valueRow(
    "Metered supply of production portfolio (MWh)",
    report.meteredSupply.quantityMWh,
    "Production quantity (MWh)",
  ),
  valueRow(
    "Metered supply of production portfolio (EUR)",
    report.meteredSupply.revenueEur,
    "Production sales revenue (EUR)",
  ),
  valueRow(
    "Production forecast (MWh)",
    report.productionForecast.forecastMWh,
    "Σ energy sold on Nord Pool (day-ahead + IDA-1…3) by the forecast sources whose net position in the MTU is a sale",
  ),
  valueRow(
    "Forecasted production sales revenue (EUR)",
    report.productionForecast.forecastSalesRevenueEur,
    "Σ (sold energy (MWh) × trade price) over the production forecast trades",
  ),
  valueRow(
    "Forecasted production average sales revenue (EUR/MWh)",
    report.productionForecast.averageSalesPriceEurPerMWh,
    "Forecasted production sales revenue (EUR) ÷ Production forecast (MWh)",
  ),
  ...(report.showsBalanceEnergy ? balanceEnergyRows(report) : []),
  {
    ...valueRow(
      "Forecast error (%)",
      report.forecastErrorPercentage,
      "(Balance energy purchased quantity + Balance energy sold quantity) ÷ Production quantity × 100",
    ),
    color: grey[100],
    bold: true,
  },
  sectionRow("Fees"),
  valueRow(
    "Service fee (EUR)",
    report.fees.serviceFeeEur,
    "Service fee: monthly + Service fee: consumption + Service fee: production",
  ),
  valueRow(
    "Service fee: monthly (EUR)",
    report.fees.monthlyServiceFeeEur,
    "Σ per day: monthly fee effective that day ÷ days in its month, over the clients with a fixed monthly fee",
  ),
  valueRow(
    "Service fee: consumption (EUR)",
    report.fees.consumptionServiceFeeEur,
    "Σ per client and MTU, by the client's fee that day: €/MWh × traded consumption MWh (TOTAL clients: metered), €/MWh × day-ahead supply plan consumption MWh, or consumption % × consumption cost limited to min/max € per MTU",
  ),
  valueRow(
    "Service fee: production (EUR)",
    report.fees.productionServiceFeeEur,
    "Σ per client and MTU, by the client's fee that day: €/MWh × traded production MWh (TOTAL clients: metered), €/MWh × day-ahead supply plan production MWh, or production % × production revenue limited to min/max € per MTU",
  ),
  valueRow(
    "Administrative cost: balance energy (EUR)",
    report.fees.balanceEnergyFeeEur,
    "Per client country: (Balance energy purchased + sold quantity (MWh)) × imbalance tariff of that country, summed over the countries",
  ),
  valueRow(
    "Administrative cost: production and consumption (EUR)",
    report.fees.meteredEnergyFeeEur,
    "Per client country: (produced + consumed energy (MWh)) × production tariff of that country, summed over the countries",
  ),
  valueRow(
    "Total revenue (EUR)",
    report.fees.totalRevenueEur,
    "Forecasted production sales revenue (EUR) + Balance energy total sales revenue (EUR)",
  ),
  valueRow(
    "Total cost (EUR)",
    report.fees.totalCostEur,
    "Consumption purchase cost (EUR) + Balance energy total purchase cost (EUR) + Service fee (EUR) + both administrative costs (EUR)",
  ),
  {
    ...valueRow(
      "Total (EUR)",
      report.fees.totalEur,
      "Total revenue (EUR) − Total cost (EUR)",
    ),
    bold: true,
  },
];
